import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Calendar } from '../components/ui/calendar';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { 
  CalendarDays, 
  Clock, 
  User, 
  Mail, 
  Phone,
  CheckCircle,
  Video,
  MapPin
} from 'lucide-react';
import axios from 'axios';
import { format, addDays, isWeekend } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const serviceOptions = [
  { value: 'home-loan', label: 'Home Loan Consultation' },
  { value: 'refinancing', label: 'Refinancing Review' },
  { value: 'investment', label: 'Investment Property Advice' },
  { value: 'first-home', label: 'First Home Buyer Guidance' },
  { value: 'commercial', label: 'Commercial Finance' },
  { value: 'general', label: 'General Enquiry' }
];

export default function BookingPage() {
  const [selectedDate, setSelectedDate] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [isLoadingSlots, setIsLoadingSlots] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [bookingComplete, setBookingComplete] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service_type: '',
    notes: ''
  });

  // Fetch available slots when date is selected
  useEffect(() => {
    if (selectedDate) {
      fetchAvailableSlots(selectedDate);
    }
  }, [selectedDate]);

  const fetchAvailableSlots = async (date) => {
    setIsLoadingSlots(true);
    setSelectedSlot(null);
    try {
      const dateStr = format(date, 'yyyy-MM-dd');
      const response = await axios.get(`${API_URL}/api/appointments/available-slots?date=${dateStr}`);
      setAvailableSlots(response.data.available_slots);
    } catch (error) {
      console.error('Error fetching slots:', error);
      // Fallback to default slots if API fails
      setAvailableSlots([
        "09:00", "09:30", "10:00", "10:30", "11:00", "11:30",
        "14:00", "14:30", "15:00", "15:30", "16:00", "16:30"
      ]);
    } finally {
      setIsLoadingSlots(false);
    }
  };

  const handleDateSelect = (date) => {
    if (date && !isWeekend(date)) {
      setSelectedDate(date);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedDate || !selectedSlot || !formData.name || !formData.email || !formData.phone || !formData.service_type) {
      toast.error('Please fill in all required fields and select a date/time');
      return;
    }

    setIsSubmitting(true);

    try {
      await axios.post(`${API_URL}/api/appointments`, {
        ...formData,
        date: format(selectedDate, 'yyyy-MM-dd'),
        time: selectedSlot
      });
      setBookingComplete(true);
      toast.success('Appointment booked successfully!');
    } catch (error) {
      console.error('Booking error:', error);
      toast.error('Failed to book appointment. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const disabledDays = (date) => {
    return isWeekend(date) || date < new Date();
  };

  if (bookingComplete) {
    return (
      <div className="pt-20 min-h-screen bg-[#F9FAFB]" data-testid="booking-page">
        <div className="max-w-2xl mx-auto px-6 py-32">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl p-12 text-center shadow-soft"
            data-testid="booking-success"
          >
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <h1 className="font-heading text-3xl font-bold text-[#374151] mb-4">
              Appointment Confirmed!
            </h1>
            <div className="bg-[#F3F4F6] rounded-xl p-6 mb-6">
              <div className="flex items-center justify-center gap-4 text-[#374151]">
                <CalendarDays className="w-5 h-5" />
                <span className="font-medium">{format(selectedDate, 'EEEE, MMMM d, yyyy')}</span>
                <Clock className="w-5 h-5 ml-4" />
                <span className="font-medium">{selectedSlot}</span>
              </div>
            </div>
            <p className="text-gray-500 mb-8">
              We've sent a confirmation email to <strong>{formData.email}</strong>. 
              One of our brokers will call you at your scheduled time.
            </p>
            <Button
              onClick={() => {
                setBookingComplete(false);
                setSelectedDate(null);
                setSelectedSlot(null);
                setFormData({ name: '', email: '', phone: '', service_type: '', notes: '' });
              }}
              variant="outline"
              className="border-2 border-[#374151] text-[#374151] hover:bg-[#374151] hover:text-white rounded-full px-8 py-6"
            >
              Book Another Appointment
            </Button>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-20" data-testid="booking-page">
      {/* Hero Section */}
      <section className="py-16 md:py-24 gradient-hero">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            <span className="inline-block px-4 py-2 bg-[#374151]/10 text-[#374151] rounded-full text-sm font-medium mb-6">
              Free Consultation
            </span>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-[#374151] leading-tight mb-6">
              Book Your Appointment
            </h1>
            <p className="text-lg md:text-xl text-gray-500 leading-relaxed">
              Schedule a free consultation with one of our mortgage experts. No obligation, just expert advice.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Booking Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-6xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Calendar */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="lg:col-span-1"
            >
              <h2 className="font-heading text-xl font-semibold text-[#374151] mb-6">
                Select a Date
              </h2>
              <div className="bg-[#F9FAFB] rounded-2xl p-4">
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={handleDateSelect}
                  disabled={disabledDays}
                  fromDate={new Date()}
                  toDate={addDays(new Date(), 60)}
                  className="rounded-xl"
                  data-testid="booking-calendar"
                />
              </div>

              {/* Time Slots */}
              {selectedDate && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-6"
                >
                  <h3 className="font-heading text-lg font-semibold text-[#374151] mb-4">
                    Available Times for {format(selectedDate, 'MMM d')}
                  </h3>
                  {isLoadingSlots ? (
                    <div className="text-center py-4 text-gray-500">Loading slots...</div>
                  ) : availableSlots.length > 0 ? (
                    <div className="grid grid-cols-3 gap-2">
                      {availableSlots.map((slot) => (
                        <button
                          key={slot}
                          onClick={() => setSelectedSlot(slot)}
                          className={`py-3 px-4 rounded-lg text-sm font-medium transition-all ${
                            selectedSlot === slot
                              ? 'bg-[#374151] text-white'
                              : 'bg-white border border-border hover:border-[#374151] text-[#374151]'
                          }`}
                          data-testid={`time-slot-${slot}`}
                        >
                          {slot}
                        </button>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-sm">No slots available for this date.</p>
                  )}
                </motion.div>
              )}
            </motion.div>

            {/* Form */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="lg:col-span-2"
            >
              <h2 className="font-heading text-xl font-semibold text-[#374151] mb-6">
                Your Details
              </h2>
              
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="booking-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-[#374151] font-medium flex items-center gap-2">
                      <User className="w-4 h-4" /> Full Name *
                    </Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      placeholder="John Smith"
                      className="h-12 rounded-lg"
                      data-testid="booking-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-[#374151] font-medium flex items-center gap-2">
                      <Mail className="w-4 h-4" /> Email Address *
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      placeholder="john@example.com"
                      className="h-12 rounded-lg"
                      data-testid="booking-email"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="phone" className="text-[#374151] font-medium flex items-center gap-2">
                      <Phone className="w-4 h-4" /> Phone Number *
                    </Label>
                    <Input
                      id="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      placeholder="0400 000 000"
                      className="h-12 rounded-lg"
                      data-testid="booking-phone"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="service" className="text-[#374151] font-medium">
                      Service Required *
                    </Label>
                    <Select onValueChange={(value) => setFormData({...formData, service_type: value})}>
                      <SelectTrigger className="h-12 rounded-lg" data-testid="booking-service">
                        <SelectValue placeholder="Select a service" />
                      </SelectTrigger>
                      <SelectContent>
                        {serviceOptions.map(option => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="notes" className="text-[#374151] font-medium">
                    Additional Notes (Optional)
                  </Label>
                  <Textarea
                    id="notes"
                    value={formData.notes}
                    onChange={(e) => setFormData({...formData, notes: e.target.value})}
                    placeholder="Tell us about your situation or any specific questions..."
                    rows={4}
                    className="rounded-lg resize-none"
                    data-testid="booking-notes"
                  />
                </div>

                {/* Selected Summary */}
                {selectedDate && selectedSlot && (
                  <div className="bg-[#F3F4F6] rounded-xl p-6">
                    <h3 className="font-heading font-semibold text-[#374151] mb-3">Your Appointment</h3>
                    <div className="flex flex-wrap gap-4 text-[#374151]">
                      <div className="flex items-center gap-2">
                        <CalendarDays className="w-5 h-5 text-[#6B7280]" />
                        <span>{format(selectedDate, 'EEEE, MMMM d, yyyy')}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Clock className="w-5 h-5 text-[#6B7280]" />
                        <span>{selectedSlot}</span>
                      </div>
                    </div>
                  </div>
                )}

                <Button
                  type="submit"
                  disabled={isSubmitting || !selectedDate || !selectedSlot}
                  className="w-full bg-[#6B7280] hover:bg-[#4B5563] text-white rounded-full py-6 text-lg font-medium transition-all duration-300 hover:-translate-y-1 hover:shadow-lg disabled:opacity-50"
                  data-testid="booking-submit"
                >
                  {isSubmitting ? 'Booking...' : 'Confirm Appointment'}
                </Button>

                <p className="text-sm text-gray-500 text-center">
                  Free consultation • No obligation • Takes 30 minutes
                </p>
              </form>

              {/* Meeting Options */}
              <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-[#F9FAFB] rounded-xl p-6 flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#374151] rounded-lg flex items-center justify-center flex-shrink-0">
                    <Video className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h4 className="font-heading font-semibold text-[#374151] mb-1">Video Call</h4>
                    <p className="text-sm text-gray-500">
                      Meet via Zoom or Google Meet from anywhere
                    </p>
                  </div>
                </div>
                <div className="bg-[#F9FAFB] rounded-xl p-6 flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#374151] rounded-lg flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h4 className="font-heading font-semibold text-[#374151] mb-1">In-Person</h4>
                    <p className="text-sm text-gray-500">
                      Visit our Melbourne CBD office
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
